import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronRight } from "lucide-react";
import { archiveCollection, fetchAdminCollection, fetchAdminCollections, removeCollectionGallery, saveCollection, uploadCollectionCover, uploadCollectionGallery } from "../api/MerchandisingAPIs";
import { fetchBrands } from "../api/BrandAPIs";
import { fetchCategories } from "../api/CategoryAPIs";
import { resolveMediaUrl } from "./Catalog/catalogUi";
import { useAuth } from "../utils/auth";
import { isAdmin } from "../utils/roles";
import { CatalogPicker, SinglePickerButton } from "./BundleSelectionPicker";
import { appendUniqueItems, collectionItemPayload, designRows, productRows, syncDesignModels, visibleFields } from "./collectionItems";

const empty = {
  name: "",
  slug: "",
  shortDescription: "",
  description: "",
  coverImageUrl: "",
  isActive: false,
  isFeatured: false,
  displayOrder: 0,
  seoTitle: "",
  seoDescription: "",
  items: [],
};

const CollectionsAdminPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canEdit = isAdmin(authState.role);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!canEdit) navigate("/dashboard");
  }, [canEdit, navigate]);
  useEffect(() => {
    if (!canEdit) return;
    Promise.all([fetchCategories().catch(() => []), fetchBrands().catch(() => [])]).then(([categories, brands]) => {
      setCatalogFilters({
        categories: (Array.isArray(categories) ? categories : []).map((item) => ({ id: item.id, name: item.name })),
        brands: (Array.isArray(brands) ? brands : []).map((item) => ({ id: item.brandId || item.id, name: item.name })),
      });
    });
  }, [canEdit]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [catalogFilters, setCatalogFilters] = useState({ categories: [], brands: [] });
  const [bulk, setBulk] = useState(null);
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["admin-collections"],
    queryFn: fetchAdminCollections,
    enabled: canEdit,
  });

  const updateItem = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
    }));
  };

  const chooseCover = (file) => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file || null);
    setCoverPreview(file ? URL.createObjectURL(file) : "");
  };

  const onSave = async () => {
    try {
      const saved = await saveCollection(editingId, {
        ...form,
        displayOrder: Number(form.displayOrder) || 0,
        gallery: (form.gallery || []).filter((item) => typeof item === "string" && !item.startsWith("blob:")),
        items: form.items.map(collectionItemPayload),
      });
      const ownerId = saved.collectionId;
      setEditingId(ownerId);
      if (coverFile) await uploadCollectionCover(ownerId, coverFile);
      if (galleryFiles.length) await uploadCollectionGallery(ownerId, galleryFiles);
      toast.success("تم حفظ المجموعة");
      chooseCover(null);
      setGalleryFiles([]);
      setForm(empty);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["admin-collections"] });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fieldClass = "box-border w-full min-h-[52px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-base leading-normal text-slate-900 placeholder:text-slate-500";

  if (!canEdit) return null;

  return (
    <div className="min-h-screen w-full bg-slate-50" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="w-full px-4 py-6 md:px-8 xl:px-10">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-3xl font-bold text-slate-900">المجموعات</h1>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-8 py-4 text-xl font-bold text-slate-800 shadow-md hover:bg-slate-100"
          >
            <ChevronRight size={28} />
            رجوع
          </button>
        </div>
        {isLoading && <p className="mb-4 text-slate-600">جاري التحميل...</p>}
        {isError && <p className="mb-4 text-red-600">تعذر تحميل المجموعات.</p>}
        <div className={`grid w-full items-start gap-6 ${data.length ? "xl:grid-cols-[320px_minmax(0,1fr)]" : ""}`}>
          {data.length > 0 && (
            <div className="space-y-3">
              {data.map((collection) => (
                <div key={collection.collectionId} className="rounded-2xl bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900">{collection.name}</p>
                      <p className="truncate text-sm text-slate-500">{collection.slug}</p>
                    </div>
                    <span className={`shrink-0 text-sm ${collection.isActive && !collection.isArchived ? "text-emerald-600" : "text-slate-400"}`}>
                      {collection.isArchived ? "مؤرشفة" : collection.isActive ? "منشورة" : "مسودة"}
                    </span>
                  </div>
                  {canEdit && (
                    <div className="mt-3 flex gap-2">
                      <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={async () => {
                        try {
                          const full = await fetchAdminCollection(collection.collectionId);
                          setEditingId(full.collectionId);
                          setForm({ ...empty, ...full, items: full.items || [] });
                        } catch (error) {
                          toast.error(error.message);
                        }
                      }}>تعديل</button>
                      <button type="button" className="rounded-lg bg-red-50 px-3 py-1 text-red-700" onClick={async () => { await archiveCollection(collection.collectionId); queryClient.invalidateQueries({ queryKey: ["admin-collections"] }); }}>أرشفة</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <form className="w-full rounded-2xl bg-white p-5 shadow-sm md:p-8" onSubmit={(event) => { event.preventDefault(); onSave(); }}>
              <h2 className="mb-6 text-2xl font-bold text-slate-900">{editingId ? "تعديل مجموعة" : "مجموعة جديدة"}</h2>
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <label className="block text-slate-800">
                  <span className="mb-2 block text-base font-semibold">الاسم</span>
                  <input className={fieldClass} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
                </label>
                <label className="block text-slate-800">
                  <span className="mb-2 block text-base font-semibold">الرابط</span>
                  <input className={fieldClass} value={form.slug || ""} onChange={(event) => setForm({ ...form, slug: event.target.value })} />
                </label>
                <label className="block text-slate-800 lg:col-span-2">
                  <span className="mb-2 block text-base font-semibold">وصف قصير</span>
                  <textarea className={`${fieldClass} min-h-[96px]`} rows={3} value={form.shortDescription || ""} onChange={(event) => setForm({ ...form, shortDescription: event.target.value })} />
                </label>
                <label className="block text-slate-800 lg:col-span-2">
                  <span className="mb-2 block text-base font-semibold">الوصف</span>
                  <textarea className={`${fieldClass} min-h-[140px]`} rows={5} value={form.description || ""} onChange={(event) => setForm({ ...form, description: event.target.value })} />
                </label>
                <label className="block text-slate-800 lg:col-span-2">
                  <span className="mb-2 block text-base font-semibold">صورة الغلاف</span>
                  {(coverPreview || form.coverImageUrl) && (
                    <img src={coverPreview || resolveMediaUrl(form.coverImageUrl)} alt="" className="mb-3 h-44 w-full max-w-md rounded-xl object-cover" />
                  )}
                  <input className={fieldClass} type="file" accept="image/*" onChange={(event) => chooseCover(event.target.files?.[0])} />
                </label>
                <div className="lg:col-span-2">
                  <span className="mb-2 block text-base font-semibold text-slate-800">صور إضافية</span>
                  <div className="mb-3 flex flex-wrap gap-3">
                    {(form.gallery || []).map((path) => (
                      <div key={path} className="relative">
                        <img src={resolveMediaUrl(path)} alt="" className="h-24 w-24 rounded-xl object-cover" />
                        <button type="button" className="mt-1 text-sm text-red-600" onClick={async () => {
                          if (!editingId) return;
                          try {
                            const result = await removeCollectionGallery(editingId, path);
                            setForm((prev) => ({ ...prev, gallery: result.gallery || [] }));
                          } catch (error) {
                            toast.error(error.message);
                          }
                        }}>حذف</button>
                      </div>
                    ))}
                    {galleryFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="text-sm text-slate-600">{file.name}</div>
                    ))}
                  </div>
                  <input className={fieldClass} type="file" accept="image/*" multiple onChange={(event) => setGalleryFiles(Array.from(event.target.files || []))} />
                </div>
                <label className="block text-slate-800">
                  <span className="mb-2 block text-base font-semibold">الترتيب</span>
                  <input className={fieldClass} type="number" value={form.displayOrder} onChange={(event) => setForm({ ...form, displayOrder: event.target.value })} />
                </label>
                <label className="block text-slate-800">
                  <span className="mb-2 block text-base font-semibold">عنوان SEO</span>
                  <input className={fieldClass} value={form.seoTitle || ""} onChange={(event) => setForm({ ...form, seoTitle: event.target.value })} />
                </label>
                <label className="block text-slate-800">
                  <span className="mb-2 block text-base font-semibold">وصف SEO</span>
                  <input className={fieldClass} value={form.seoDescription || ""} onChange={(event) => setForm({ ...form, seoDescription: event.target.value })} />
                </label>
              </div>
              <div className="mt-5 flex flex-wrap gap-6 text-base text-slate-800">
                <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} /> نشر</label>
                <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={form.isFeatured} onChange={(event) => setForm({ ...form, isFeatured: event.target.checked })} /> مميزة</label>
              </div>
              <div className="mt-8 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-lg font-bold text-slate-900">عناصر المجموعة</h3>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="rounded-xl bg-purple-50 px-4 py-2 text-purple-800" onClick={() => setBulk({ mode: "designs" })}>إضافة تصاميم</button>
                    <button type="button" className="rounded-xl bg-purple-50 px-4 py-2 text-purple-800" onClick={() => setBulk({ mode: "products" })}>إضافة منتجات</button>
                    <button type="button" className="rounded-xl bg-slate-100 px-4 py-2 text-slate-800" onClick={() => setForm({ ...form, items: [...form.items, { itemKind: "design", isActive: true }] })}>إضافة عنصر</button>
                  </div>
                </div>
                {form.items.map((item, index) => {
                  const fields = visibleFields(item.itemKind);
                  return (
                    <div key={index} className="space-y-3 rounded-xl bg-slate-50 p-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="min-w-[180px] flex-1 text-slate-800">
                          <span className="mb-1 block text-sm font-semibold">النوع</span>
                          <select value={item.itemKind || "design"} onChange={(event) => updateItem(index, { itemKind: event.target.value })} className={fieldClass}>
                            <option value="design">تصميم</option>
                            <option value="design-type">تصميم وموديل</option>
                            <option value="product">منتج جاهز</option>
                            <option value="variant">متغير منتج</option>
                          </select>
                        </label>
                        <label className="flex items-center gap-2 pt-6">
                          <input type="checkbox" className="h-5 w-5" checked={item.isActive !== false} onChange={(event) => updateItem(index, { isActive: event.target.checked })} />
                          ظاهر
                        </label>
                        <button type="button" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-red-700" onClick={() => setForm({ ...form, items: form.items.filter((_, itemIndex) => itemIndex !== index) })}>حذف</button>
                      </div>
                      {fields.includes("design") && (
                        <SinglePickerButton label="التصميم" kind="design" selectedId={item.designId} filters={catalogFilters} onChange={(designId) => updateItem(index, { designId })} />
                      )}
                      {fields.includes("phone") && (
                        <SinglePickerButton label="موديل الموبايل" kind="phone" selectedId={item.typeId} filters={catalogFilters} onChange={(typeId) => updateItem(index, { typeId, caseStyleId: null })} />
                      )}
                      {fields.includes("caseStyle") && item.typeId && (
                        <SinglePickerButton label="شكل الجراب" kind="caseStyle" selectedId={item.caseStyleId} filters={{ typeId: item.typeId }} onChange={(caseStyleId) => updateItem(index, { caseStyleId })} />
                      )}
                      {fields.includes("caseStyle") && !item.typeId && (
                        <p className="text-sm text-slate-500">اختَر موديل الموبايل الأول، وبعدها تظهر أشكال الجراب المناسبة له.</p>
                      )}
                      {fields.includes("phone") && (
                        <button type="button" className="rounded-lg bg-slate-100 px-3 py-2 disabled:opacity-40" disabled={!item.designId} onClick={() => setBulk({ mode: "phones", designId: item.designId })}>أضف نفس التصميم لموديلات تانية</button>
                      )}
                      {fields.includes("product") && (
                        <SinglePickerButton label="المنتج" kind="product" selectedId={item.productId} filters={catalogFilters} onChange={(productId) => updateItem(index, { productId })} />
                      )}
                      {fields.includes("variant") && (
                        <>
                          <SinglePickerButton label="المنتج، عشان تظهر متغيراته" kind="product" selectedId={item.parentProductId || item.productId} filters={catalogFilters} onChange={(productId) => updateItem(index, { parentProductId: productId, productVariantId: null })} />
                          <SinglePickerButton label="المتغير" kind="variant" selectedId={item.productVariantId} filters={{ productId: item.parentProductId || item.productId || undefined }} onChange={(productVariantId) => updateItem(index, { productVariantId })} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {bulk && (
                <CatalogPicker
                  title={bulk.mode === "products" ? "المنتجات" : bulk.mode === "phones" ? "موديلات الموبايل" : "التصاميم"}
                  kind={bulk.mode === "products" ? "product" : bulk.mode === "phones" ? "phone" : "design"}
                  filters={bulk.mode === "phones" ? { brands: catalogFilters.brands } : catalogFilters}
                  selectedIds={bulk.mode === "phones" ? form.items.filter((item) => item.itemKind === "design-type" && Number(item.designId) === Number(bulk.designId)).map((item) => Number(item.typeId)) : []}
                  onClose={() => setBulk(null)}
                  onConfirm={(ids) => {
                    setForm((prev) => ({
                      ...prev,
                      items: bulk.mode === "phones"
                        ? syncDesignModels(prev.items, bulk.designId, ids)
                        : appendUniqueItems(prev.items, bulk.mode === "products" ? productRows(ids) : designRows(ids)),
                    }));
                    setBulk(null);
                  }}
                />
              )}
              <button type="submit" className="mt-8 w-full rounded-xl bg-purple-700 py-4 text-lg font-bold text-white">حفظ</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionsAdminPage;
